import * as React from "react";
import { useState } from "react";
import Box from "@mui/material/Box";
import { Grid, useTheme, IconButton } from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ItemFormModal from "../../components/form/ItemFormModal";
import { currencyFormatter } from "../../utils/currencyFormat";
import { lineItemCalculation } from "../../utils/pricingEngineForDocuments";

const ItemDetailsTable = ({
  items = [],
  remove,
  replace,
  readOnly,
  transactionType = null,
}) => {
  const theme = useTheme();
  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;

  const handleEditClick = (item) => {
    handleOpen();
    setCurrentItem(item);
  };

  const handleViewClick = (item) => {
    handleOpen();
    setCurrentItem(item);
  };

  const handleDeleteClick = (params) => {
    const rowIndex = items.findIndex((item) => item.key === params.row.key);
    remove(rowIndex);
  };

  const replaceItem = (item) => {
    const updatedItems = items.map((lineItem) =>
      lineItem.key === item.key ? { ...lineItem, ...item } : lineItem,
    );

    replace(updatedItems);
    handleClose();
  };

  const columns = [
    {
      headerName: "Item Name",
      field: "name",
      flex: 1,
    },
    {
      field: "quantity",
      headerName: "Quantity",
      flex: 1,
    },
    {
      field: "price",
      headerName: "Unit Price",
      flex: 1,
      renderCell: (params) => {
        // Backward compatibility: support both old (price_amount) and new (base_amount) structures
        const baseAmount =
          params.value?.base_amount ||
          params.value?.allowance?.base_amount ||
          1;
        const allowanceAmount = params.value?.allowance?.amount || 0;
        const priceAmount = baseAmount - allowanceAmount;
        return currencyFormatter().format(priceAmount || 0);
      },
    },
    {
      field: "allowance_amount",
      headerName: "Allowance",
      flex: 1,
      renderCell: (params) => {
        const { allowance_amount } = lineItemCalculation(params?.row);
        return `${currencyFormatter().format(allowance_amount || 0)}`;
      },
    },
    {
      field: "charge_amount",
      headerName: "Charge",
      flex: 1,
      renderCell: (params) => {
        const { charge_amount } = lineItemCalculation(params?.row);
        return `${currencyFormatter().format(charge_amount || 0)}`;
      },
    },
    {
      field: "net_amount",
      headerName: "Total Before Tax",
      flex: 1,
      valueGetter: (params) => {
        return currencyFormatter().format(params);
      },
    },
    {
      field: "tax_amount",
      headerName: "Tax",
      flex: 1,
      renderCell: (params) => {
        const tax = params.row;
        const rate = tax.classified_tax_category?.percent ?? tax.tax_rate;
        const { tax_amount } = lineItemCalculation(params.row);
        return `${currencyFormatter().format(tax_amount)} ${rate ? `(${rate}%)` : ""}`;
      },
    },
    {
      field: "total_amount",
      headerName: "Total Amount",
      flex: 1,
      valueGetter: (params) => {
        return currencyFormatter().format(params);
      },
    },
  ];

  const commonActionColumn = {
    field: "actions",
    headerName: "Actions",
    headerClassName: "sticky-header-last",
    cellClassName: "sticky-header-last",
    width: 80,
    sortable: false,
    filterable: false,
  };

  if (readOnly) {
    columns.push({
      ...commonActionColumn,
      renderCell: (params) => (
        <IconButton
          onClick={() => handleViewClick(params.row)}
          size="small"
          data-testid={`view-item-${params.id}`}
          sx={{ color: theme.palette.primary.main }}
        >
          <VisibilityIcon sx={{ fontSize: 16 }} />
        </IconButton>
      ),
    });
  } else {
    columns.push({
      ...commonActionColumn,
      type: "actions",
      getActions: (params) => [
        <GridActionsCellItem
          key={`edit-${params.id}`}
          label="Edit"
          onClick={() => handleEditClick(params.row)}
          showInMenu
          data-testid={`edit-item-${params.id}`}
        />,
        <GridActionsCellItem
          key={`remove-${params.id}`}
          label="Remove"
          onClick={() => {
            handleDeleteClick(params);
          }}
          showInMenu
          data-testid={`remove-item-${params.id}`}
        />,
      ],
    });
  }

  return (
    <Grid container>
      <Grid size={12}>
        <Box
          sx={{
            mt: 1,
            width: "100%",
            overflow: "auto",
            display: "grid",
          }}
        >
          <DataGrid
            rows={items ?? []}
            getRowId={(row) => row.id || row._id}
            columns={columns}
            rowCount={items.length}
            paginationMode="server"
            hideFooter
            style={{ width: "100%" }}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 100,
                },
              },
            }}
            pageSizeOptions={[10, 25]}
            disableColumnMenu
            disableColumnSorting
            disableVirtualization
            disableRowSelectionOnClick
            sx={{
              overflow: "auto",
              "& .sticky-header-last, & .sticky-cell-last": {
                position: "sticky",
                right: -1,
                zIndex: 4,
                backgroundColor: "#fff",
                "& .MuiButtonBase-root": {
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                },
              },
            }}
          />
        </Box>
      </Grid>
      <ItemFormModal
        open={open}
        handleClose={handleClose}
        handleSubmit={replaceItem}
        initialData={currentItem}
        edit
        readOnly={readOnly}
        transactionType={transactionType}
      />
    </Grid>
  );
};

export default ItemDetailsTable;
